import { Component, ChangeDetectionStrategy, OnChanges, Input, Output, EventEmitter, SimpleChanges } from '@angular/core';
import { IndexCard, UgerapportSager, UgerapportKunder, TimeregLine, Timereg } from '@store/models';
import { UntypedFormGroup, UntypedFormArray, UntypedFormBuilder, UntypedFormControl, Validators } from '@angular/forms';
import { ConfirmService } from 'app/shared/confirm-dialog/confirm-dialog.service';
import { AlertService } from 'app/core/alerts/alert.service';
import { inputTimeToDecimal } from 'app/shared/time';
import { LuxonUtils } from '@shared';

@Component({
    selector: 'app-time-log-lines-ui',
    templateUrl: './time-log-lines.component.html',
    styleUrls: ['../timetrack.shared.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class TimeLogLinesComponent implements OnChanges {

    form: UntypedFormGroup;

    @Input() timeregsDate: TimeregLine[];
    @Input() ugerapportSager: UgerapportSager[];
    @Input() ugerapportKunder: UgerapportKunder[];
    @Input() ugerepDoneDate: boolean;
    @Input() selectedDate: Date;
    @Input() user: IndexCard;
    @Output() save = new EventEmitter<Partial<Timereg>>();
    @Output() update = new EventEmitter<Timereg>();
    @Output() delete = new EventEmitter<number>();

    editId: number[] = [0];
    displayColumns = ['dato', 'kunde', 'sag', 'beskrivelse', 'timer', 'funktioner'];

    get totalTimer() {
        return this.timeregsDate.reduce((acc, curr) => acc + (curr.timer || 0), 0);
    }

    get utcSelectedDate() {
        return LuxonUtils.utc(this.selectedDate).startOf('day').toJSDate();
    }

    get newEntryIndex() {
        const formArray = this.form.get('timeregs') as UntypedFormArray;
        const index = formArray.value.findIndex((tr: TimeregLine) => tr.id === 0) as number;
        return index;
    }

    get formTimeregs() {
        return this.form.get('timeregs') as UntypedFormArray;
    }

    constructor(
        private fb: UntypedFormBuilder,
        private alert: AlertService,
        private confirm: ConfirmService
    ) { }

    ngOnChanges(changes: SimpleChanges) {
        if (changes.timeregsDate) {
            this.createForm();
            this.timeregsDate.map(tr => this.formTimeregs.controls.push(this.createEntry(tr)));
        }
        if (!this.ugerepDoneDate) {
            this.createNewEntry();
        } else {
            this.removeNewEntry();
        }
    }

    createForm() {
        this.form = this.fb.group({
            timeregs: this.fb.array([])
        });
    }

    createNewEntry() {
        if (this.newEntryIndex === -1) {
            this.formTimeregs.controls.push(this.createEntry({
                ...new TimeregLine(),
                dato: this.utcSelectedDate
            }));
            this.editId.push(0);
        }
    }

    removeNewEntry() {
        if (this.newEntryIndex >= 0) {
            this.formTimeregs.removeAt(this.newEntryIndex);
        }
        this.editId = this.editId.filter(id => id !== 0);
    }

    createEntry(tr: TimeregLine) {
        return this.fb.group({
            id: tr.id,
            medId: tr.medId,
            sagID: [tr.sagID, Validators.required],
            dato: tr.dato,
            timer: [tr.timer, Validators.required],
            beskrivelse: [tr.beskrivelse, Validators.maxLength(255)],
            timesats: tr.timesats,
            kontor: tr.kontor,
            registreret: tr.registreret,
            belob: tr.belob,
            uret: tr.uret,
            kid: tr.kid,
            label: tr.label,
            sagBeskrivelse: tr.sagBeskrivelse,
            done: tr.done
        });
    }

    trackById(_: number, tr: TimeregLine) {
        return tr.id;
    }

    isEdit(id: number): boolean {
        return this.editId.indexOf(id) > -1;
    }

    onEdit(timereg: TimeregLine) {
        this.editId.push(timereg.id);
    }

    onDelete(timereg: TimeregLine) {
        this.confirm.question(
            `Er du sikker på du vil slette posten "${timereg.beskrivelse}" med ${timereg.timer} timer?`,
            'Ja',
            'Nej'
        ).then(yes => {
            if (yes) { this.delete.emit(timereg.id); }
        });
    }

    onSave(tr: UntypedFormControl) {
        if (tr.invalid) {
            this.alert.createAlert('Der er fejl eller mangler i linjen!', 'danger');
            return;
        }
        const saveTr: Timereg = tr.value;
        if (this.ugerepDoneDate) {
            this.alert.createAlert('Dagen er lukket!', 'danger');
            return;
        }
        saveTr.uret = true;
        const convertedTime = inputTimeToDecimal(saveTr.timer.toString());
        if (isNaN(+convertedTime)) {
            this.alert.createAlert('Der er fejl i den indtastede time værdi', 'danger');
            return;
        }
        saveTr.timer = +convertedTime;
        if (saveTr.id === 0) {
            tr.reset({id: 0, dato: this.utcSelectedDate});
            saveTr.medId = this.user.cardId;
            saveTr.kontor = this.user.kontor;
        }
        this.editId = this.editId.filter(id => id === 0 || id !== saveTr.id);
        this.save.emit(saveTr);
    }

    onCancel(tr: UntypedFormControl) {
        if (tr.value.id === 0) {
            tr.reset({id: 0, dato: this.utcSelectedDate});
        } else {
            const orgTr = this.timeregsDate.find(t => t.id === tr.value.id);
            tr.patchValue(orgTr);
            this.editId = this.editId.filter(e => e !== tr.value.id);
        }
    }

    onClientChange(tr: UntypedFormControl) {
        tr.patchValue({sagID: undefined});
    }

}
